import { useState, useCallback, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, LogOut } from "lucide-react";
import { Wordmark } from "@stockuj/ui";
import { supabase } from "@/lib/supabase";
import { useOrganization } from "@/hooks/useOrganization";
import { useSubscriptionDetails } from "@/hooks/useSubscriptionDetails";
import { useLocations } from "@/hooks/useLocations";
import { useInitLocation } from "@/hooks/useInitLocation";
import { useLocationStore } from "@/stores/useLocationStore";
import { AutoOrderBadge } from "./AutoOrderBadge";
import { LocationSwitcher } from "./LocationSwitcher";
import { TrialBanner } from "./TrialBanner";

const NAV_ITEMS = [
  { to: "/", label: "Magazyn" },
  { to: "/zamowienia", label: "Zamówienia" },
  { to: "/menu", label: "Menu" },
  { to: "/cennik", label: "Cennik" },
  { to: "/ustawienia", label: "Ustawienia" },
  { to: "/konto", label: "Konto" },
] as const;

export function AppShell() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Location switcher data
  const { data: org } = useOrganization();
  const { data: locations } = useLocations(org?.organizationId);
  useInitLocation(locations);
  const canManage = org?.role === "owner" || org?.role === "manager";
  const showSwitcher = (locations?.length ?? 0) > 1 || canManage;

  // "Cennik" in the nav is an upsell entry — show it only to an owner who
  // could act on it: an owner without an active paid subscription. Hidden for
  // paying owners (dead marketing page) and for staff/viewers (not their call).
  // NOTE: `trialing` is deliberately NOT treated as active here — a trialing
  // owner should still see Cennik to pick a paid plan before the trial ends.
  // This intentionally differs from pricing.tsx, where `trialing` counts as
  // active for feature gating. Different goal: nav upsell vs feature access.
  const { data: subscription } = useSubscriptionDetails(org?.organizationId);
  const subscriptionResolved = subscription !== undefined;
  const hasActiveSub = !!subscription && subscription.status === "active";
  const showPricing = org?.role === "owner" && subscriptionResolved && !hasActiveSub;
  const navItems = NAV_ITEMS.filter((item) => item.to !== "/cennik" || showPricing);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const handleLogout = useCallback(async () => {
    setDrawerOpen(false);

    // Clear React Query cache BEFORE signOut (prevents stale data refetch)
    queryClient.clear();

    // Clear location store so next user starts fresh
    useLocationStore.getState().clear();

    // Clean up any stale recovery-related sessionStorage
    sessionStorage.removeItem("supabase-recovery-hash");

    // Sign out with global scope (invalidates on all devices/tabs)
    await supabase.auth.signOut({ scope: "global" });

    // Hard reload — wipes ALL React state, sessionStorage, query cache
    window.location.href = "/auth/login";
  }, [queryClient]);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-40 border-b border-border bg-white pt-[env(safe-area-inset-top)]">
        <div className="flex h-14 items-center px-4 md:px-6">
          {/* Wordmark — smaller on mobile */}
          <div className="md:hidden">
            <Wordmark size={24} />
          </div>
          <div className="hidden md:block">
            <Wordmark size={28} />
          </div>

          {/* Desktop nav */}
          <nav className="ml-8 hidden gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-button px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-navy text-white"
                      : "text-gray-muted hover:bg-gray-faint hover:text-navy"
                  }`
                }
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop right side */}
          <div className="ml-auto hidden items-center gap-3 md:flex">
            {showSwitcher && locations && (
              <LocationSwitcher locations={locations} canViewAll={canManage} />
            )}
            <AutoOrderBadge />
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-button px-3 py-1.5 text-sm font-medium text-gray-muted transition-colors hover:bg-gray-faint hover:text-navy"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {t("auth.logout")}
            </button>
          </div>

          {/* Mobile hamburger */}
          <div className="ml-auto flex items-center gap-2 md:hidden">
            <AutoOrderBadge />
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-button text-navy transition-colors hover:bg-gray-faint"
              aria-label="Otwórz menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-50 bg-navy/30 backdrop-blur-sm"
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
            />

            {/* Drawer panel */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed right-0 top-0 z-50 flex h-full w-[280px] max-w-[85vw] flex-col bg-white shadow-subtle-lg"
              role="dialog"
              aria-modal="true"
              aria-label="Menu nawigacji"
              style={{ paddingTop: "env(safe-area-inset-top)" }}
            >
              {/* Drawer header */}
              <div className="flex h-14 items-center justify-between border-b border-border px-4">
                <Wordmark size={24} />
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="flex h-11 w-11 items-center justify-center rounded-button text-navy transition-colors hover:bg-gray-faint"
                  aria-label="Zamknij menu"
                  autoFocus
                >
                  <X size={22} />
                </button>
              </div>

              {/* Nav items */}
              <nav className="flex-1 px-3 py-4">
                <div className="space-y-1">
                  {navItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === "/"}
                      className={({ isActive }) =>
                        `flex min-h-[44px] items-center rounded-button px-4 py-3 text-base font-medium transition-colors ${
                          isActive ? "bg-navy text-white" : "text-navy hover:bg-gray-faint"
                        }`
                      }
                      style={{ fontFamily: "Inter, sans-serif" }}
                    >
                      {item.label}
                      {item.to === "/zamowienia" && (
                        <span className="ml-auto">
                          <AutoOrderBadge />
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>

                {/* Location switcher (mobile) */}
                {showSwitcher && locations && (
                  <>
                    <div className="my-4 border-t border-border" />
                    <div className="px-4 py-2">
                      <LocationSwitcher locations={locations} canViewAll={canManage} />
                    </div>
                  </>
                )}

                {/* Separator */}
                <div className="my-4 border-t border-border" />

                {/* Logout */}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex min-h-[44px] w-full items-center gap-3 rounded-button px-4 py-3 text-base font-medium text-gray-muted transition-colors hover:bg-gray-faint hover:text-navy"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <LogOut size={18} />
                  {t("auth.logout")}
                </button>
              </nav>

              {/* Safe area bottom padding */}
              <div style={{ paddingBottom: "env(safe-area-inset-bottom)" }} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <TrialBanner />

      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
